// ============================================================================
// Web Beacon
// ----------------------------------------------------------------------------
// Tracks page loads, tel: clicks, form activity, phone-number swaps,
// Podium, Schedule Engine, and ServiceTitan Scheduling Pro events,
// and fires lightweight pixel requests.
//
// Designed to be served same-origin with its beacon endpoint, so tenants
// onboarded to a first-party CNAME (e.g. t-polaris.acme.com) fire beacons
// back to their own domain. A legacy fallback keeps direct-embed installs
// working even if the script tag origin can't be resolved.
// ============================================================================

import {
  generateUUID,
  readCookies,
  writeCookie,
  refreshSessionCookie as refreshSession,
  resolveEndpoint,
  registrableDomain,
  isOriginUnder,
  redactSensitive,
} from './utils.js';

const FALLBACK_ENDPOINT = "https://horn.rynopowered.com/v2/e";
const EVENTS_ENDPOINT = resolveEndpoint(document.currentScript, FALLBACK_ENDPOINT);

// Scope cookies to the registrable domain so visitor/session continuity
// follows a real person across every subdomain of the site. Null on
// localhost / IPs / single-label hosts — we fall back to host-only scope.
const COOKIE_DOMAIN = registrableDomain(window.location.hostname);

// Debug mode — toggle from the console with wbDebug() or wbDebug(false).
// Initial state comes from `data-debug` on the script tag.
window.__webBeaconDebug = !!(document.currentScript && document.currentScript.hasAttribute("data-debug"));
window.wbDebug = (enabled = true) => {
  window.__webBeaconDebug = enabled;
  console.log(`[Web Beacon] Event logging ${enabled ? 'ON' : 'OFF'}`);
};

// Install sentinel so the onboarder (and anything else) can verify which
// version is live and which endpoint it resolved to.
window.__webBeacon = {
  version: SCRIPT_VERSION,
  endpoint: EVENTS_ENDPOINT,
};

// ---------------------------------------------------------------------------
// Cookie handling (visitor id + visit counter + session)
// ---------------------------------------------------------------------------

const VISITOR_COOKIE = "__wabpx_vid";
const VISIT_COUNT_COOKIE = "__wabpx_vc";
const SESSION_COOKIE = "__wabpx_sid";
const SESSION_DURATION_MINUTES = 30;

function refreshSessionCookie(sessionId) {
  refreshSession(sessionId, SESSION_COOKIE, SESSION_DURATION_MINUTES, COOKIE_DOMAIN);
}

function getOrCreateSessionId() {
  const cookies = readCookies();
  let sessionId = cookies[SESSION_COOKIE];
  if (!sessionId) {
    sessionId = generateUUID();
  }
  refreshSessionCookie(sessionId);
  return sessionId;
}

function initVisitorCookies() {
  const cookies = readCookies();
  const visitorId = cookies[VISITOR_COOKIE] || generateUUID();
  const previousCount = Number(cookies[VISIT_COUNT_COOKIE] || 0);
  const visitCount = previousCount + 1 > 99999 ? 0 : previousCount + 1;

  writeCookie(VISITOR_COOKIE, visitorId, 365, COOKIE_DOMAIN);
  writeCookie(VISIT_COUNT_COOKIE, String(visitCount), 365, COOKIE_DOMAIN);

  return { visitorId, visitCount };
}

// ---------------------------------------------------------------------------
// Tracking pixel sender
// ---------------------------------------------------------------------------

function extractInvocaId(raw) {
  if (typeof raw !== 'string' || raw[0] !== '{') return raw;
  try {
    const parsed = JSON.parse(raw);
    if (parsed.session && parsed.session.invoca_id) return parsed.session.invoca_id;
  } catch (_) { /* not JSON or malformed */ }
  return "";
}

function getCallTrackingUID() {
  const cookies = readCookies();
  if (cookies.calltrk_session_id) return extractInvocaId(cookies.calltrk_session_id);
  try {
    if (window.Invoca && window.Invoca.Tools && typeof window.Invoca.Tools.readInvocaData === 'function') {
      const data = window.Invoca.Tools.readInvocaData();
      if (data && data.InvocaID) return extractInvocaId(data.InvocaID);
    }
  } catch (_) { /* Invoca not available */ }
  if (cookies.invoca_session) return extractInvocaId(cookies.invoca_session);
  return "";
}

function getCrmUID() {
  const cookies = readCookies();
  if (cookies.hubspotutk) return cookies.hubspotutk;
  return "";
}

function sendTrackingPixel(eventCategory, eventName, eventDetail, identity, m1 = "", m2 = "", m3 = "") {
  refreshSessionCookie(identity.sessionId);

  const eventId = generateUUID();

  const queryString = new URLSearchParams({
    // Event
    x: eventId,                // event id
    t: eventName,              // event type/name
    d: eventDetail,            // event detail/value
    e: eventCategory,          // event category
    // Identity
    s: identity.sessionId,     // session id
    i: identity.visitorId,     // visitor id
    c: identity.visitCount,    // visit counter
    // Context
    l: window.location.href,   // page URL
    r: document.referrer,      // referrer
    // Third-party UIDs
    ct: getCallTrackingUID(),  // call tracking UID
    crm: getCrmUID(),          // CRM UID
    // Metadata (meaning varies by category)
    m1: m1,
    m2: m2,
    m3: m3,
    // Script bundle version, e.g. "1.1.0". Lets us slice Doris by which
    // version is running per tenant — answers "is the rollout complete?".
    v: SCRIPT_VERSION
  }).toString();
  const fullUrl = `${EVENTS_ENDPOINT}?${queryString}`;

  if (window.__webBeaconDebug) {
    console.log(
      `[Web Beacon] %c${eventCategory}%c → %c${eventName}`,
      'color: #888',
      'color: inherit',
      'color: #2196F3; font-weight: bold',
      '\n  detail:', eventDetail || '(none)',
      '\n  event_id:', eventId,
      '\n  session:', identity.sessionId,
      '\n  visitor:', identity.visitorId,
      '\n  url:', window.location.href
    );
  }

  if (navigator.sendBeacon) {
    navigator.sendBeacon(fullUrl);
  } else {
    const img = new Image();
    img.referrerPolicy = "strict-origin-when-cross-origin";
    img.src = fullUrl;
  }
}

// ---------------------------------------------------------------------------
// DOM event tracking
// ---------------------------------------------------------------------------

function attachTelClickTracking(identity) {
  document.body.querySelectorAll('a[href^="tel:"]').forEach(link => {
    if (link.__wbTelBound) return;
    link.__wbTelBound = true;
    link.addEventListener("click", () => {
      sendTrackingPixel("phone", "click-to-call", link.href, identity);
    });
  });
}

function attachFormTracking(identity) {
  document.body.querySelectorAll("form input, form textarea").forEach(el => {
    if (el.__wbInputBound) return;
    el.__wbInputBound = true;
    if (el.type.toLowerCase() === "password") return;
    el.addEventListener("change", () => {
      const form = el.closest("form");
      const formId = form ? (form.id || form.getAttribute("name") || "") : "";
      const fieldName = el.name || el.id || "";
      // Redact SSNs and Luhn-valid CCs before they leave the browser. Passwords
      // are already excluded above; this catches cases where a sensitive value
      // ends up in a non-password field (e.g. a quote form with a "Last 4 of
      // SSN" text input, or a mis-typed CC in a notes field).
      const value = redactSensitive(el.value || "");
      sendTrackingPixel("form", "input-change", value, identity, formId, fieldName);
    });
  });

  document.body.querySelectorAll("form button, form input[type=submit]").forEach(btn => {
    if (btn.__wbButtonBound) return;
    btn.__wbButtonBound = true;
    btn.addEventListener("click", () => {
      const idOrFirstClass = btn.id || (btn.className.split(/\s+/)[0] || "");
      const form = btn.closest("form");
      const formId = form ? (form.id || form.getAttribute("name") || "") : "";
      sendTrackingPixel("form", "button-click", idOrFirstClass, identity, formId);
    });
  });
}

function observePhoneSwaps(identity) {
  let lastDigits = "";
  let lastTime = 0;

  const observer = new MutationObserver(mutations => {
    mutations.forEach(m => {
      if (m.attributeName !== "href") return;
      const oldHref = m.oldValue || "";
      const newHref = m.target.getAttribute("href") || "";
      if (oldHref === newHref) return;
      if (!/^tel:/i.test(newHref)) return;

      const digits = newHref.replace(/\D/g, "").replace(/^1/, "");
      const now = Date.now();
      const tooSoon = now - lastTime < 1000;
      const same = digits === lastDigits;
      if (!tooSoon || !same) {
        lastDigits = digits;
        lastTime = now;
        sendTrackingPixel("phone", "phone-swap", `${oldHref}/${newHref}`, identity);
      }
    });
  });

  observer.observe(document.body, {
    subtree: true,
    attributes: true,
    attributeOldValue: true,
    attributeFilter: ["href"]
  });
}

function observeDomAdditions(identity) {
  const observer = new MutationObserver(mutations => {
    mutations.forEach(m => {
      m.addedNodes.forEach(node => {
        if (!(node instanceof Element)) return;
        attachTelClickTracking(identity);
        attachFormTracking(identity);
      });
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

// ---------------------------------------------------------------------------
// Third-party integrations
// ---------------------------------------------------------------------------

function wirePodium(identity) {
  window.PodiumEventsCallback = (eventName, { session_id } = {}) => {
    const cleanName = String(eventName || "").replace(/\s+/g, "-").toLowerCase();
    sendTrackingPixel("podium", cleanName, session_id || "", identity);
  };
}

function wireScheduleEngine(identity) {
  // Observed hostnames in production (from Watchtower scans, April 2026):
  //   embed.scheduleengine.net, webchat.scheduleengine.net
  // Narrow to .scheduleengine.net so a forged message from e.g.
  // scheduleengine.attacker.com can't inject events.
  window.addEventListener("message", evt => {
    if (!isOriginUnder(String(evt.origin || ""), "scheduleengine.net")) return;
    const data = evt.data || {};

    if (data.type === "se-event") {
      sendTrackingPixel("schedule-engine-booking", data.eventName || "", "", identity);
    } else if (data.messageType) {
      sendTrackingPixel("schedule-engine-chat", data.messageType || "", "", identity);
    }
  });
}

function wireServiceTitanSchedulingPro(identity) {
  // Observed hostnames in production (from Watchtower scans, April 2026):
  //   embed.scheduler.servicetitan.com, static.servicetitan.com
  window.addEventListener("message", evt => {
    if (!isOriginUnder(String(evt.origin || ""), "servicetitan.com")) return;
    const data = evt.data || {};
    if (data.type !== "se-send-client-google-analytics") return;

    const ga = data.ga || {};
    sendTrackingPixel(
      "st-scheduling-pro",
      ga.action || "",
      ga.sid || "",
      identity,
      ga.label || "",
      ga.schedulerName || "",
      ga.value || ""
    );
  });
}

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

(function startWebBeacon() {
  const identity = initVisitorCookies();
  identity.sessionId = getOrCreateSessionId();

  // Refresh session on any click
  document.addEventListener("click", () => {
    refreshSessionCookie(identity.sessionId);
  });

  sendTrackingPixel("pageload", "pageload", "", identity);

  const initialize = () => {
    attachTelClickTracking(identity);
    attachFormTracking(identity);
    observePhoneSwaps(identity);
    observeDomAdditions(identity);
    wirePodium(identity);
    wireScheduleEngine(identity);
    wireServiceTitanSchedulingPro(identity);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }
})();
